import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, ShoppingCart, ExternalLink, FileText, Printer, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useServiceOrder, useUpdateServiceOrderStatus, useGenerateServiceOrderQuote, useDeleteServiceOrder } from '@/hooks/useServiceOrders';
import { useServiceOrderPhotos } from '@/hooks/useServiceOrderPhotos';
import { useServiceModuleConfig } from '@/hooks/useServiceModuleConfig';
import { ServiceOrderStatusBadge } from '@/components/Services/ServiceOrders/ServiceOrderStatusBadge';
import ServiceOrderTransitionButtons from '@/components/Services/ServiceOrders/ServiceOrderTransitionButtons';
import ServiceOrderPhotoGrid from '@/components/Services/ServiceOrders/ServiceOrderPhotoGrid';
import ServiceOrderEquipmentList from '@/components/Services/ServiceOrders/ServiceOrderEquipmentList';
import ServiceOrderReceiptButton from '@/components/Services/PDF/ServiceOrderReceiptButton';
import { ServiceOrderServiceDocumentButton } from '@/components/Services/PDF/ServiceOrderServiceDocumentButton';
import { ConvertToSaleDialog } from '@/components/Services/ServiceOrders/ConvertToSaleDialog';
import { ServiceOrderQuotePrintDialog } from '@/components/Services/ServiceOrderQuotePrintDialog';
import { ServiceOrderSignatureDialog } from '@/components/Services/ServiceOrderSignatureDialog';
import { SERVICE_ORDER_TRANSITIONS, ServiceOrderStatusTransition } from '@/types/service';
import { formatCurrency } from '@/utils/formatters';

export default function ServiceOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canAccess, company, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  // ── Todos os hooks ANTES de qualquer early return ──────────────────
  const { data: order, isLoading, error } = useServiceOrder(id ?? null);
  const { data: photos = [], uploading, uploadPhoto, deletePhoto } =
    useServiceOrderPhotos(id ?? null);
  const { data: moduleConfig } = useServiceModuleConfig();
  const updateStatus = useUpdateServiceOrderStatus();
  const [pendingTransition, setPendingTransition] =
    useState<ServiceOrderStatusTransition | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [printQuoteOpen, setPrintQuoteOpen] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const generateQuote = useGenerateServiceOrderQuote();
  const deleteMutation = useDeleteServiceOrder();
  // ──────────────────────────────────────────────────────────────────

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">
          Ordem de serviço não encontrada.
        </p>
        <Button variant="outline" onClick={() => navigate('/dashboard/services')}>
          Voltar para lista
        </Button>
      </div>
    );
  }

  const availableTransitions = SERVICE_ORDER_TRANSITIONS.filter(
    (t) => t.from === order.status && canAccess(t.requiresPermission),
  );

  const equipmentLabel  = moduleConfig?.equipment_label  ?? 'Equipamento';
  const equipmentPlural = moduleConfig?.equipment_plural ?? 'Equipamentos';

  async function handleConfirmTransition() {
    if (!pendingTransition || !id) return;
    setTransitioning(true);
    try {
      await updateStatus.mutateAsync({
        id,
        status: pendingTransition.to,
      });
    } catch {
      /* feedback no onError do hook */
    } finally {
      setTransitioning(false);
      setPendingTransition(null);
    }
  }

  const handleGenerateQuote = async () => {
    const payload = (order.service_order_equipment ?? []).map((eq) => ({
      equipment_id: eq.id,
      items: (eq.service_order_items ?? [])
        .filter((it) => it.item_type === 'service' ? !!it.service_id : !!it.product_id)
        .map((it, index) => ({
          item_type:   it.item_type,
          product_id:  it.item_type === 'service' ? undefined : it.product_id,
          service_id:  it.item_type === 'service' ? it.service_id : undefined,
          description: it.description || undefined,
          quantity:    it.quantity,
          unit_price:  it.unit_price,
          position:    index,
        })),
    }));
    await generateQuote.mutateAsync({ id: order.id, equipment: payload });
    // Força refetch antes de navegar: garante que o editor não leia quotation_id=null do cache velho
    await queryClient.refetchQueries({ queryKey: ['service-order', order.id] });
    navigate(`/dashboard/services/${order.id}/orcamento`);
  };

  const isCancelled   = order.status === 'cancelled';
  const equipment     = order.service_order_equipment ?? [];
  const totalValue    = order.total_value   ?? 0;
  const totalParts    = order.total_parts   ?? 0;
  const totalServices = order.total_services ?? 0;
  const customerName  = order.customers?.name ?? 'Cliente não informado';

  return (
    <>
      <div className="flex flex-col min-h-screen bg-background">
        {/* Header */}
        <div className="border-b bg-card px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard/services')}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-semibold">
                  {order.service_order_number}
                </h1>
                <ServiceOrderStatusBadge status={order.status} />
                {order.sale_id && (
                  <span className="text-xs text-muted-foreground ml-1">
                    → Venda vinculada
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{customerName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <ServiceOrderReceiptButton order={order} />
            {!isCancelled && !order.signature_data && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSignatureOpen(true)}
              >
                <FileText className="h-4 w-4 mr-2" />
                Assinar Coleta
              </Button>
            )}
            {order.quotation_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPrintQuoteOpen(true)}
              >
                <Printer className="h-4 w-4 mr-2" />
                Imprimir Orçamento
              </Button>
            )}
            {(order.status === 'completed' || order.status === 'billed') && (
              <ServiceOrderServiceDocumentButton
                serviceOrderId={order.id}
                serviceOrder={{
                  service_order_number: order.service_order_number,
                  created_at:           order.created_at ?? '',
                  actual_delivery_date: order.actual_delivery_date ?? null,
                  notes:                order.notes ?? null,
                  total_value:          order.total_value,
                  total_parts:          order.total_parts,
                  total_services:       order.total_services,
                }}
                customer={{
                  name:     customerName,
                  document: order.customers?.document_number ?? null,
                  phone:    order.customers?.phone ?? null,
                  email:    order.customers?.email ?? null,
                }}
                equipment={equipment.map((eq) => ({
                  brand:          eq.brand,
                  model:          eq.model,
                  serial_number:  eq.serial_number,
                  reported_issue: eq.reported_issue,
                  items: eq.service_order_items.map((item) => ({
                    description: item.description ?? '',
                    quantity:    item.quantity,
                    unit_price:  item.unit_price,
                    total_price: item.total_price ?? item.unit_price * item.quantity,
                    item_type:   item.item_type === 'service' ? 'service' : 'part',
                  })),
                }))}
                company={{
                  name:    company?.name ?? '',
                  cnpj:    company?.cnpj ?? null,
                  address: company?.endereco ?? null,
                  phone:   company?.telefone ?? null,
                }}
              />
            )}
            {order.sale_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/dashboard/sales/${order.sale_id}`)}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Venda
              </Button>
            )}
            {!isCancelled && order.status === 'draft' && !order.quotation_id && canAccess('services:create') && (
              <Button
                variant="default"
                size="sm"
                onClick={handleGenerateQuote}
                disabled={generateQuote.isPending}
              >
                {generateQuote.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Gerando…</>
                ) : (
                  <><FileText className="h-4 w-4 mr-2" />Gerar Orçamento</>
                )}
              </Button>
            )}
            {!isCancelled && order.quotation_id && canAccess('services:create') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/dashboard/services/${order.id}/orcamento`)}
              >
                <FileText className="h-4 w-4 mr-2" />
                Editar Orçamento
              </Button>
            )}
            {!isCancelled && canAccess('services:convert') &&
              (order.status === 'in_progress' || order.status === 'completed') && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setConvertDialogOpen(true)}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Converter para Venda
              </Button>
            )}
            {!isCancelled && (
              <ServiceOrderTransitionButtons
                transitions={availableTransitions}
                onTransition={setPendingTransition}
              />
            )}
            {!order.sale_id && canAccess('services:order_delete') && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir OS
              </Button>
            )}
          </div>
        </div>

        {/* Corpo */}
        <div className="flex-1 p-6">
          <Tabs defaultValue="overview">
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Visão Geral</TabsTrigger>
              <TabsTrigger value="equipment">
                {equipmentPlural}
              </TabsTrigger>
              <TabsTrigger value="photos">
                Fotos{photos.length > 0 ? ` (${photos.length})` : ''}
              </TabsTrigger>
            </TabsList>

            {/* Aba: Visão Geral */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Dados da OS</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Número</span>
                      <span className="font-medium">
                        {order.service_order_number}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Responsável</span>
                      <span>{order.responsible_name ?? '—'}</span>
                    </div>
                    {order.collected_by && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Coletado por</span>
                        <span>{order.collected_by}</span>
                      </div>
                    )}
                    {order.expected_delivery_date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Entrega prevista
                        </span>
                        <span>
                          {new Date(order.expected_delivery_date)
                            .toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    )}
                    {order.actual_delivery_date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Entrega real
                        </span>
                        <span>
                          {new Date(order.actual_delivery_date)
                            .toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Valores</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {totalParts > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Peças</span>
                        <span>{formatCurrency(totalParts)}</span>
                      </div>
                    )}
                    {totalServices > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Serviços</span>
                        <span>{formatCurrency(totalServices)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold border-t pt-2">
                      <span>Total</span>
                      <span>{formatCurrency(totalValue)}</span>
                    </div>
                  </CardContent>
                </Card>

                {order.notes && (
                  <Card className="md:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-sm">Observações</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap">
                        {order.notes}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {order.internal_notes && (
                  <Card className="md:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-sm">
                        Observações Internas
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm whitespace-pre-wrap">
                        {order.internal_notes}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Aba: Equipamentos */}
            <TabsContent value="equipment">
              <ServiceOrderEquipmentList
                equipment={equipment}
                equipmentLabel={equipmentLabel}
                showExtraAttributes={
                  moduleConfig?.show_extra_attributes ?? false
                }
                extraAttributeLabels={
                  (moduleConfig?.extra_attribute_labels as Array<{
                    key: string;
                    label: string;
                    type: 'text' | 'number' | 'date';
                  }>) ?? []
                }
              />
            </TabsContent>

            {/* Aba: Fotos */}
            <TabsContent value="photos">
              <ServiceOrderPhotoGrid
                photos={photos}
                serviceOrderId={id!}
                uploading={uploading}
                onUpload={(file, caption) =>
                  uploadPhoto.mutate({
                    file,
                    caption,
                    serviceOrderId: id!,
                  })
                }
                onDelete={(photo) =>
                  deletePhoto.mutate({ photo, soId: id! })
                }
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Dialog de conversão para venda */}
      <ConvertToSaleDialog
        open={convertDialogOpen}
        onOpenChange={setConvertDialogOpen}
        serviceOrder={{
          id:                   order.id,
          service_order_number: order.service_order_number,
          total_value:          order.total_value,
          customer_id:          order.customer_id,
        }}
        companyId={company?.id ?? ''}
      />

      {/* Dialog de impressão do orçamento vinculado (agrupado por equipamento) */}
      <ServiceOrderQuotePrintDialog
        open={printQuoteOpen}
        onOpenChange={setPrintQuoteOpen}
        serviceOrderId={order.id}
      />

      {/* Dialog de assinatura da coleta */}
      <ServiceOrderSignatureDialog
        open={signatureOpen}
        onOpenChange={setSignatureOpen}
        order={order}
        onSignatureComplete={() => {
          setSignatureOpen(false);
          queryClient.invalidateQueries({ queryKey: ['service-order', order.id] });
        }}
      />

      {/* AlertDialog de confirmação de exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta OS?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteMutation.mutate(order.id, {
                  onSuccess: () => {
                    setDeleteDialogOpen(false);
                    navigate('/dashboard/services');
                  },
                });
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Excluindo…</>
              ) : (
                'Excluir'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog de confirmação de transição */}
      <AlertDialog
        open={!!pendingTransition}
        onOpenChange={(open) => {
          if (!open) setPendingTransition(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingTransition?.label}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingTransition?.confirmMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={transitioning}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmTransition}
              disabled={transitioning}
            >
              {transitioning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processando…
                </>
              ) : (
                'Confirmar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
